// src/components/authentication/AccessDenied.js
/**
 * AccessDenied
 * ------------
 * Deterministic "you are signed in, but not authorised for the dashboard"
 * screen (DASH-AUTH-1).
 *
 * Shown when an authenticated Firebase user does not hold dashboard membership
 * — e.g. a patient account from the shared Firebase project. Because the
 * dashboard and patient app share one Firebase project, simply being signed in
 * is not sufficient; membership is confirmed by the backend User Service.
 *
 * Variants:
 *   • `reason="denied"`  → not authorised (default). Offers sign-out.
 *   • `reason="error"`   → membership check could not be completed. Offers retry
 *                          plus sign-out, so a transient backend/network failure
 *                          does not look like a permanent denial.
 *
 * Props
 * @param {{
 *   reason?: "denied" | "error",
 *   onRetry?: () => void,
 * }} props
 */

import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { firebaseApp } from "../../firebase/firebaseApp";
import RedeemInviteForm from "./RedeemInviteForm";
import Spacer from "../Spacer";

export default function AccessDenied({ reason = "denied", onRetry }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSignOut = useCallback(() => {
    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err),
      );
  }, [navigate]);

  const isError = reason === "error";

  const title = isError
    ? t("accessDenied.errorTitle", "Couldn’t verify your access")
    : t("accessDenied.title", "Access denied");

  const message = isError
    ? t(
        "accessDenied.errorMessage",
        "We couldn’t confirm your dashboard access right now. Please try again, or sign out and sign in with an authorised account.",
      )
    : t(
        "accessDenied.message",
        "This account is not authorised to access the dashboard. If you believe this is a mistake, contact your administrator, or sign out and sign in with an authorised account.",
      );

  return (
    <div
      role="alert"
      className="m-auto flex max-w-md flex-col items-center gap-4 p-4 text-start"
    >
      <h1 className="title-text">{title}</h1>
      <p className="muted-text">{message}</p>

      {/* A signed-in but unauthorised user can onboard in place by redeeming an
          access code. On success the gate re-evaluates and admits them. */}
      {!isError && (
        <>
          <Spacer />
          <div className="w-full">
            <RedeemInviteForm
              onSuccess={(membership) =>
                navigate(
                  membership?.organisationRole === "admin"
                    ? "/admin"
                    : "/patients",
                  { replace: true },
                )
              }
            />
          </div>
        </>
      )}

      <div className="flex flex-col items-center gap-4 w-full">
        <Spacer />

        {isError && typeof onRetry === "function" && (
          <button type="button" onClick={onRetry} className="primary-button">
            {t("accessDenied.retry", "Try again")}
          </button>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className="outlined-button !w-full"
        >
          {t("accessDenied.signOut", "Sign out")}
        </button>
      </div>
    </div>
  );
}
